import { Component, Inject, inject, Input, OnInit } from '@angular/core';
import { AuthType } from '@nano/core/auth';
import { CONFIG } from '@nano/core/config';
import { TfaUsage } from '@nano/core/tfa';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { L10N_LOCALE, L10nLocale, L10nTranslationService } from 'angular-l10n';
import { NzModalRef } from 'ng-zorro-antd/modal';
import { Observable, Subject, Subscription } from 'rxjs';
import { Payment, PaymentSummary, PaymentType, isPaymentCurrencyExchange } from '../payment';
import { AlertService } from '@nano/common/alert';
import { SseService } from '@nano/core/sse';
import { FeeTranslationParams, PaymentStatus } from '@nano/payment/payment';
import { PaymentService } from '@nano/payment/payment.service';
import { indicateLoading } from '@nano/common/util';
import { SentryLastActionService } from '../../core/integration/sentry/sentry-last-action.service';

@UntilDestroy()
@Component({
  selector: 'app-payment-confirm',
  templateUrl: './payment-confirm.component.html',
  styleUrls: ['./payment-confirm.component.less'],
  standalone: false
})
export class PaymentConfirmComponent implements OnInit {
  @Input() paymentStatus?: PaymentStatus;

  readonly paymentSummary$: Observable<PaymentSummary>;
  readonly paymentType = PaymentType;
  private exchangeRateSseSubscription?: Subscription;
  readonly showIbanInPaymentAccountSelection = inject(CONFIG).showIbanInPaymentAccountSelection;
  readonly tfaUsage = TfaUsage.whenConfirmingOperation;
  readonly indicateLoading$: Subject<boolean> = new Subject();
  readonly submitButtonDisabled$: Observable<boolean>;
  readonly submitButtonLoading$: Subject<boolean> = new Subject();
  payment: PaymentSummary;
  private isMobileSignature = false;

  constructor(
    @Inject(L10N_LOCALE) public locale: L10nLocale,
    private modal: NzModalRef,
    private alertService: AlertService,
    private sseService: SseService,
    public paymentService: PaymentService,
    private translationService: L10nTranslationService,
    private sentryLastActionService: SentryLastActionService
  ) {
    this.paymentSummary$ = this.paymentService.paymentSummary$;
  }

  ngOnInit(): void {
    this.paymentSummary$.pipe(untilDestroyed(this)).subscribe((payment) => this.handlePaymentSummary(payment));
  }

  private handlePaymentSummary(payment: PaymentSummary): void {
    this.payment = payment;
    if (this.paymentStatus !== PaymentStatus.CREATED) return;

    this.isMobileSignature = (payment.securityMeansType || payment.confirmationType) === AuthType.MOBILE_SIGNATURE;

    this.handleExchangeRateChangeForGatewayPayment(payment);
  }

  private handleExchangeRateChangeForGatewayPayment(payment: PaymentSummary): void {
    if (payment.operationType === PaymentType.PAYMENT_GATEWAY && !!payment.exchangeRate) {
      if (payment.exchangeRate !== this.paymentService.getConditionsExchangeRate) {
        this.showExchangeRateAlert();
        this.paymentService.updatePaymentSummaryOnExchangeRateChange(payment);
      }
      this.subscribeToExchangeRateSse();
    }
  }

  private subscribeToExchangeRateSse(): void {
    this.exchangeRateSseSubscription?.unsubscribe();

    this.exchangeRateSseSubscription = this.sseService.exchangeRateChanged$.pipe(untilDestroyed(this)).subscribe(() => {
      this.showExchangeRateAlert();
      this.paymentService.updatePaymentSummaryOnExchangeRateChange(this.payment);
    });
  }

  isPaymentCurrencyExchange(payment: Payment): boolean {
    return isPaymentCurrencyExchange(payment.operationType);
  }

  confirmed(): void {
    this.sentryLastActionService.setPaymentAction('confirm', this.payment?.operationType);
    this.paymentService.markPaymentConfirmed();
  }

  canceled(): void {
    if (this.isMobileSignature) {
      this.paymentService.backToInitialStep();
    } else {
      this.modal.close();
    }
  }

  private showExchangeRateAlert(): void {
    this.alertService.alert({
      messageType: 'key',
      message: 'payment.exchangeRateChanged',
      icon: 'alert-circle',
      type: 'warning'
    });
  }

  displayExchangeAmount(payment: Payment): boolean {
    return payment.operationType === PaymentType.PAYMENT_GATEWAY && !!payment.initialAmount && !!payment.initialAmount && !!payment.exchangeRate;
  }

  get primaryAmount(): number {
    return this.displayExchangeAmount(this.payment) ? this.payment.initialAmount : this.payment.amount;
  }

  get primaryCurrency(): string {
    return this.displayExchangeAmount(this.payment) ? this.payment.initialCurrency : this.payment.currency;
  }

  get feeSameCurrency(): boolean {
    const feeCurrency = this.payment?.fee?.currency;
    return this.displayExchangeAmount(this.payment) ? feeCurrency === this.payment.initialCurrency : feeCurrency === this.payment.currency;
  }

  get feeTranslationKey(): string {
    const isTiered = this.payment?.tieredFeesApplied;
    const sameCurrency = this.feeSameCurrency;

    if (isTiered && sameCurrency) return 'payment.amount.extraWithTilde';
    if (isTiered && !sameCurrency) return 'payment.amount.extraDifferentCurrenciesWithTilde';
    if (!isTiered && sameCurrency) return 'payment.amount.extra';
    return 'payment.amount.extraDifferentCurrencies';
  }

  get feeTranslationParams(): FeeTranslationParams {
    const fee = this.payment?.fee;
    const total = this.payment?.total;
    const isSameCurrency = this.feeSameCurrency;

    return isSameCurrency
      ? {
          total: total?.amount,
          fee: fee?.amount
        }
      : {
          amount: this.primaryAmount,
          fee: fee?.amount
        };
  }

  isPayementStatusCreated(): boolean {
    return this.paymentStatus === PaymentStatus.CREATED;
  }

  get beneficiaryBankIdentifierLabel(): string {
    return this.translate('payment.beneficiaryBankIdentifier.label');
  }

  private translate(key: string): string {
    return this.translationService.translate(key, this.locale.language) as string;
  }

  acceptSuggestedAddress(): void {
    const paymentWithSuggestedAddress = { ...this.payment };
    const suggestedAddress = this.paymentService.suggestedBeneficiaryAddress?.address;

    if (suggestedAddress) {
      if (suggestedAddress.addressLine) paymentWithSuggestedAddress.beneficiaryAddress = suggestedAddress.addressLine;
      if (suggestedAddress.buildingName) paymentWithSuggestedAddress.beneficiaryBuildingName = suggestedAddress.buildingName;
      if (suggestedAddress.buildingNumber) paymentWithSuggestedAddress.beneficiaryBuildingNumber = suggestedAddress.buildingNumber;
      if (suggestedAddress.city) paymentWithSuggestedAddress.beneficiaryCity = suggestedAddress.city;
      if (suggestedAddress.country) paymentWithSuggestedAddress.beneficiaryCountry = suggestedAddress.country;
      if (suggestedAddress.countrySubDivision) paymentWithSuggestedAddress.beneficiaryProvince = suggestedAddress.countrySubDivision;
      if (suggestedAddress.postCode) paymentWithSuggestedAddress.beneficiaryPostalCode = suggestedAddress.postCode;
      if (suggestedAddress.street) paymentWithSuggestedAddress.beneficiaryStreetName = suggestedAddress.street;
    }

    if (this.payment.saveForLater) {
      this.paymentService
        .savePaymentForLaterConfirmation(paymentWithSuggestedAddress)
        .pipe(indicateLoading(this.submitButtonLoading$), indicateLoading(this.indicateLoading$))
        .subscribe();
    } else {
      this.paymentService
        .createPaymentOperation(paymentWithSuggestedAddress)
        .pipe(indicateLoading(this.submitButtonLoading$), indicateLoading(this.indicateLoading$))
        .subscribe();
    }
  }

  rejectSuggestedAddress(): void {
    if (this.payment.saveForLater) {
      this.paymentService
        .savePaymentForLaterConfirmation(this.payment)
        .pipe(indicateLoading(this.submitButtonLoading$), indicateLoading(this.indicateLoading$))
        .subscribe();
    } else {
      this.paymentService
        .createPaymentOperation(this.payment)
        .pipe(indicateLoading(this.submitButtonLoading$), indicateLoading(this.indicateLoading$))
        .subscribe();
    }
  }
}
